import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClioService,
  MatterFolderNode,
  MatterFolderStructureResponse,
  MatterFolderTemplateItem,
  MatterPreviewResponse,
} from "../../api";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { debounce } from "@mui/material/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const INDENT_PER_LEVEL = 22;
const ROW_ICON_SLOT = 28;

function collectPaths(nodes: MatterFolderNode[], parentPath = ""): string[] {
  return nodes.flatMap((node) => {
    const path = parentPath ? `${parentPath}/${node.name}` : node.name;
    return node.children?.length > 0 ? [path, ...collectPaths(node.children, path)] : [];
  });
}

/** The template alone is rendered through the same tree, without existence status. */
function templateToNodes(items: MatterFolderTemplateItem[]): MatterFolderNode[] {
  return items.map((item) => ({
    name: item.name,
    existsInClio: false,
    inTemplate: true,
    children: templateToNodes(item.children ?? []),
  }));
}

type FolderTreeProps = {
  nodes: MatterFolderNode[];
  collapsed: Set<string>;
  onToggle: (path: string) => void;
  showStatus: boolean;
  depth?: number;
  parentPath?: string;
};

function FolderTree({
  nodes,
  collapsed,
  onToggle,
  showStatus,
  depth = 0,
  parentPath = "",
}: FolderTreeProps) {
  return (
    <>
      {nodes.map((node) => {
        const path = parentPath ? `${parentPath}/${node.name}` : node.name;
        const hasChildren = node.children?.length > 0;
        const isCollapsed = collapsed.has(path);
        const missing = showStatus && !node.existsInClio;

        return (
          <Box key={path}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              onClick={hasChildren ? () => onToggle(path) : undefined}
              sx={{
                pl: `${depth * INDENT_PER_LEVEL + 8}px`,
                pr: 1.5,
                py: 0.5,
                borderRadius: 1,
                cursor: hasChildren ? "pointer" : "default",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  sx={{ width: ROW_ICON_SLOT, height: ROW_ICON_SLOT, p: 0 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggle(path);
                  }}
                >
                  {isCollapsed ? (
                    <ChevronRightIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: ROW_ICON_SLOT, flexShrink: 0 }} />
              )}

              {missing ? (
                <FolderOutlinedIcon fontSize="small" sx={{ color: "warning.main" }} />
              ) : (
                <FolderRoundedIcon
                  fontSize="small"
                  sx={{ color: depth === 0 ? "primary.main" : "primary.light" }}
                />
              )}

              <Typography
                variant="body2"
                sx={{
                  fontWeight: depth === 0 ? 600 : 400,
                  color: missing ? "text.secondary" : "text.primary",
                }}
              >
                {node.name}
              </Typography>

              {showStatus && !node.existsInClio && (
                <Chip
                  label="Missing"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
              {showStatus && node.existsInClio && !node.inTemplate && (
                <Chip
                  label="Not in template"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              )}
            </Stack>

            {hasChildren && (
              <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                <FolderTree
                  nodes={node.children}
                  collapsed={collapsed}
                  onToggle={onToggle}
                  showStatus={showStatus}
                  depth={depth + 1}
                  parentPath={path}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </>
  );
}

export default function MatterFolderStructure() {
  const [matterOptions, setMatterOptions] = useState<readonly MatterPreviewResponse[]>([]);
  const [matterValue, setMatterValue] = useState<MatterPreviewResponse | null>(null);
  const [matterInputValue, setMatterInputValue] = useState("");
  const [template, setTemplate] = useState<MatterFolderNode[]>([]);
  const [structure, setStructure] = useState<MatterFolderStructureResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    ClioService.getApiClioMatterFolderStructureTemplate().then((items) => {
      const nodes = templateToNodes(items);
      setTemplate(nodes);
      setCollapsed(new Set(collectPaths(nodes)));
    });
  }, []);

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioMatters(input).then((response) => {
          const newOptions: MatterPreviewResponse[] = matterValue ? [matterValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setMatterOptions(newOptions);
        });
      }, 200),
    [matterValue]
  );

  useEffect(() => {
    if (matterInputValue === "") {
      setMatterOptions(matterValue ? [matterValue] : []);
      return;
    }
    fetchMatters(matterInputValue);
  }, [matterValue, matterInputValue, fetchMatters]);

  const matterId = matterValue?.id ?? null;

  const loadStructure = useCallback(
    async (preserveExpansion = false) => {
      if (matterId == null) {
        setStructure(null);
        return;
      }

      setLoading(true);
      try {
        const response = await ClioService.getApiClioMatterFolderStructure(+matterId);
        setStructure(response);
        if (!preserveExpansion) {
          // Collapse everything below the top level so the overview stays readable.
          setCollapsed(
            new Set(collectPaths(response.folders).filter((path) => path.includes("/")))
          );
        }
      } catch {
        setStructure(null);
        MySwal.fire({
          icon: "error",
          title: <p>Error loading folder structure.</p>,
        });
      } finally {
        setLoading(false);
      }
    },
    [matterId]
  );

  useEffect(() => {
    loadStructure();
  }, [loadStructure]);

  const toggleNode = (path: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });

  const visibleNodes = structure ? structure.folders : template;
  const expandablePaths = useMemo(() => collectPaths(visibleNodes), [visibleNodes]);
  const allExpanded = collapsed.size === 0;

  const createMissingFolders = async () => {
    if (matterId == null || structure == null) {
      return;
    }

    const confirmation = await MySwal.fire({
      icon: "question",
      title: <p>Create missing folders?</p>,
      text: `${structure.missingCount} folder(s) will be created in Clio for "${matterValue?.description}". Existing folders are left untouched.`,
      showCancelButton: true,
      confirmButtonText: "Create",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await ClioService.postApiClioEnsureMatterFolderStructure(+matterId);
      await loadStructure(true);
      MySwal.fire({
        icon: "success",
        title: <p>Folder structure created.</p>,
        text: `${result.createdFolders.length} folder(s) created, ${result.skippedFolders.length} already existed.`,
      });
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error creating folder structure.</p>,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" className="title" fontWeight={600}>
          Matter Folder Structure
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Matters created in Clio receive the default folder structure automatically. Select
          a matter to review its current folders and create anything that is missing.
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="flex-start"
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            width: { xs: "100%", md: 440 },
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Select matter
            </Typography>
            <Tooltip title="Reload folder structure">
              <span>
                <IconButton
                  size="small"
                  onClick={() => loadStructure(true)}
                  disabled={matterId == null || loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Autocomplete
            fullWidth
            getOptionLabel={(option) =>
              (typeof option === "string" ? option : option.description) as string
            }
            filterOptions={(x) => x}
            options={matterOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={matterValue}
            noOptionsText="Start typing matter"
            onChange={(_, selectedValue) => setMatterValue(selectedValue)}
            onInputChange={(_, newInputValue) => setMatterInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Matter" required fullWidth size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{option.description}</Typography>
              </li>
            )}
          />

          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={createMissingFolders}
            disabled={
              matterId == null || loading || submitting || structure?.missingCount === 0
            }
            startIcon={
              submitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CreateNewFolderOutlinedIcon />
              )
            }
            sx={{ mt: 2.5, height: 48 }}
          >
            {structure && structure.missingCount > 0
              ? `Create ${structure.missingCount} Missing Folders`
              : "Create Missing Folders"}
          </Button>

          {structure && !loading && structure.missingCount === 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              This matter already has the complete default folder structure.
            </Alert>
          )}

          {structure && !loading && structure.existingCount === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This matter has no folders in Clio yet. The full default structure will be
              created.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              {structure ? "Current folder structure" : "Default folder template"}
            </Typography>

            {structure && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FolderRoundedIcon fontSize="small" sx={{ color: "primary.main" }} />
                  <Typography variant="caption" color="text.secondary">
                    In Clio
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FolderOutlinedIcon fontSize="small" sx={{ color: "warning.main" }} />
                  <Typography variant="caption" color="text.secondary">
                    Missing from Clio
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small"
              startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              onClick={() =>
                setCollapsed(allExpanded ? new Set(expandablePaths) : new Set())
              }
              disabled={visibleNodes.length === 0}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          </Box>

          <Divider />

          <Box sx={{ p: 1.5 }}>
            {loading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Loading folders from Clio...
                </Typography>
              </Box>
            ) : visibleNodes.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 4, textAlign: "center" }}
              >
                No folders to display.
              </Typography>
            ) : (
              <>
                {!structure && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 1, pb: 1.5 }}
                  >
                    Select a matter to compare this template against its folders in Clio.
                  </Typography>
                )}
                <FolderTree
                  nodes={visibleNodes}
                  collapsed={collapsed}
                  onToggle={toggleNode}
                  showStatus={structure != null}
                />
              </>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
